import reduce from 'lodash/reduce';
import keys from 'lodash/keys';
import pickBy from 'lodash/pickBy';
import { useEffect, useRef, useState } from 'react';
import { FormState, UseFormWatch } from 'react-hook-form';

// Since react-hook-form does not have an onChange event, we use this custom hook instead to as a similar trigger whenever react-hook-form state changes.
// Note: This is not optimal as it will also trigger on mount and conflict with values that we try to inject from backend. Therefore we will skip triggering onChange on first mount by using the mounted state.

interface GetValidFieldsProps<T extends Record<string, unknown>> {
  data: T;
  exclude: string[];
  include: (keyof T)[];
}

const getValidFields = <T extends Record<string, unknown>>({
  data,
  exclude,
  include,
}: GetValidFieldsProps<T>) => {
  return pickBy(
    data,
    (_, key) => !exclude.includes(key) && include.includes(key)
  );
};

const getRemovedFields = <FormState>({
  currentFields,
  previousFields,
}: {
  currentFields: (keyof FormState)[];
  previousFields: (keyof FormState)[];
}) => {
  return reduce(
    previousFields,
    (acc, key) => {
      if (!currentFields.includes(key)) {
        return { ...acc, [key]: null };
      }
      return acc;
    },
    {} as Partial<FormState>
  );
};

interface UseOnValueChangeProps<T extends Record<string, unknown>> {
  onChange: (data: T) => void;
  watch: UseFormWatch<T>;
  formState: FormState<T>;
  touchedFieldsOverride?: (keyof T)[];
}

export const useOnValueChange = <T extends Record<string, unknown>>({
  onChange,
  watch,
  formState: { isValidating, errors, touchedFields },
  touchedFieldsOverride = [],
}: UseOnValueChangeProps<T>) => {
  const previousFields = useRef<string[]>();
  const [mounted, setMounted] = useState(false);

  const data = watch();

  // Simulate onChange event
  useEffect(() => {
    if (!mounted) {
      return setMounted(true);
    }
    if (isValidating) {
      return;
    }

    const validFields = getValidFields({
      data,
      exclude: keys(errors),
      include: [...keys(touchedFields), ...touchedFieldsOverride],
    }) as T;

    const removedFields =
      previousFields.current &&
      getRemovedFields({
        currentFields: keys(data),
        previousFields: previousFields.current,
      });

    previousFields.current = keys(data);
    onChange({ ...validFields, ...removedFields });
  }, [JSON.stringify(data), mounted, isValidating]);
};
